import type { StoryblokRichtext, StoryblokRichtextContent } from "storyblok-rich-text-react-renderer"

import type { Nullish } from "~/@types/generics"

/**
 * function that truncates a Storyblok Richtext object based on a maximum character limit.
 * @param {StoryblokRichtext | null | undefined} richtext - The Storyblok Richtext object to truncate.
 * @param {number} maxChars - The maximum number of characters to truncate the Storyblok Richtext object to.
 * @param {string} [suffix="..."] - The suffix to append to the truncated Storyblok Richtext object.
 * @returns {StoryblokRichtext | null} The truncated Storyblok Richtext object.
 */

export function truncateRichtext<T extends StoryblokRichtext>(richtext: T, maxChars: number, suffix?: string): T
export function truncateRichtext<T extends StoryblokRichtext>(
  richtext: Nullish<T>,
  maxChars: number,
  suffix?: string
): T | null
export function truncateRichtext<T extends StoryblokRichtext>(
  richtext: Nullish<T>,
  maxChars: number,
  suffix = "..."
): T | null {
  if (!richtext) return null

  const content = truncateContent(richtext, maxChars, suffix)
  return content.data
}

type Truncate<Data extends StoryblokRichtextContent | StoryblokRichtext> = {
  success: boolean
  data: Data
  maxChars: number
}

export function truncateText<T extends StoryblokRichtextContent>(
  richtext: T,
  maxChars: number,
  suffix = "..."
): Truncate<T> {
  const count = richtext.text?.replace(" ", "").length || 0
  const diff = maxChars - count

  if (diff <= 0) {
    const text = richtext?.text?.slice(0, maxChars)

    return {
      success: true,
      data: { ...richtext, ...(text ? { text: `${text.trimEnd()}${suffix}` } : {}) },
      maxChars: 0,
    }
  }

  return { success: false, data: richtext, maxChars: diff }
}

function truncateContent<T extends StoryblokRichtextContent | StoryblokRichtext>(
  richtext: T,
  maxChars: number,
  suffix = "..."
): Truncate<T> {
  let success = false
  let count = maxChars

  const content = richtext?.content
    ?.map((item) => {
      if (success) return null

      if ("text" in item) {
        const truncateElement = truncateText(item, count, suffix)
        count = truncateElement.maxChars
        if (truncateElement.success) success = true

        return truncateElement.data
      }

      if ("content" in item) {
        const truncateElement = truncateContent(item, count, suffix)
        count = truncateElement.maxChars
        if (truncateElement.success) success = true

        return truncateElement.data
      }
    })
    .filter(Boolean)

  return {
    success,
    data: { ...richtext, ...(content ? { content } : {}) },
    maxChars: count,
  }
}
